// frontend/src/features/portfolio/lib/portfolioSummary.js
/**
 * Detail-page summary for one client (the /client/:id page).
 *
 * Read-through, per-client cache: the first view of a client fetches and
 * memoizes; repeat views are instant until the entry goes stale (see
 * expireStalePortfolioSummaries). The backend warms
 * these server-side during login (and nightly via prewarm_reports.py), so the
 * first fetch usually lands on a warm Athena memo / result-reuse rather than a
 * true-cold scan. The cache is in-memory module state (never persisted) and is
 * cleared on logout so a second user on the same tab never sees the previous
 * user's holdings.
 */
import { API_ENDPOINTS } from '../../../config/api';
import { PRELOAD_PORTFOLIO_CODENAMES } from '../../../config/constants';
import { loadPortfolioOptions } from '../../reports/lib/portfolioOptions';
import { isStale } from '../../../utils/cacheFreshness';

function authHeader() {
  const token =
    localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const cacheByClient = new Map(); // clientId -> resolved summary payload
const readAtByClient = new Map(); // clientId -> when that payload was read (ms epoch)
const inflightByClient = new Map(); // clientId -> in-flight Promise (dedupe)

/**
 * Resolve the detail summary for a client, reusing the cache (or an in-flight
 * request). Pass `{ force: true }` to bypass the cache and refetch (e.g. Retry).
 * Signature is backward compatible: `fetchPortfolioSummary(id)` still works.
 */
export function fetchPortfolioSummary(clientId, { force = false } = {}) {
  const key = String(clientId);
  if (!force && cacheByClient.has(key)) return Promise.resolve(cacheByClient.get(key));
  if (!force && inflightByClient.has(key)) return inflightByClient.get(key);
  const p = fetch(API_ENDPOINTS.reports.portfolioSummary(clientId), { headers: authHeader() })
    .then((res) => {
      if (!res.ok) throw new Error(`portfolio summary ${res.status}`);
      return res.json();
    })
    .then((data) => {
      cacheByClient.set(key, data);
      readAtByClient.set(key, Date.now());
      inflightByClient.delete(key);
      return data;
    })
    .catch((err) => {
      // Clear inflight on failure too, so a failed fetch never wedges the key -
      // a later view (or Retry) starts a fresh request instead of re-awaiting it.
      inflightByClient.delete(key);
      throw err;
    });
  inflightByClient.set(key, p);
  return p;
}

/** Drop all cached summaries + any in-flight entries (call on logout / token clear). */
export function clearPortfolioSummary() {
  cacheByClient.clear();
  readAtByClient.clear();
  inflightByClient.clear();
}

/**
 * The freshness refresher for this cache (see utils/cacheFreshness): drop every
 * summary that has gone stale so the next view of that client fetches it again.
 * Evicted, not re-read in the background - there is one entry per client ever
 * viewed, and re-reading them all every few minutes would be a fetch storm.
 */
export function expireStalePortfolioSummaries() {
  readAtByClient.forEach((readAt, key) => {
    if (!isStale(readAt)) return;
    cacheByClient.delete(key);
    readAtByClient.delete(key);
  });
}

// How many login-time summary warms may be in flight at once. Each summary can
// be a multi-second Athena read when cold; a full burst would stampede the pool.
const PRELOAD_CONCURRENCY = 3;

/**
 * Warm the summary cache for the desk's pinned client families (see
 * PRELOAD_PORTFOLIO_CODENAMES) as soon as the user is in the authenticated app,
 * so those portfolio detail pages render with zero fetch. Codenames resolve to
 * client ids through the shared login-preloaded /codenames payload. Fire and
 * forget: never rejects, and one failed warm doesn't block the rest.
 */
export function preloadPortfolioSummaries() {
  return loadPortfolioOptions()
    .then((data) => {
      const wanted = new Set(PRELOAD_PORTFOLIO_CODENAMES.map((c) => c.toUpperCase()));
      const ids = (data.clients || [])
        .filter((c) => wanted.has(String(c.codename || '').replace(/^@/, '').toUpperCase()))
        .map((c) => c.client_id);
      const queue = [...ids];
      const drain = () => {
        const id = queue.shift();
        if (id === undefined) return Promise.resolve();
        return fetchPortfolioSummary(id).catch(() => {}).then(drain);
      };
      return Promise.all(Array.from({ length: PRELOAD_CONCURRENCY }, drain));
    })
    .then(() => undefined)
    .catch(() => {});
}
